/**
 * Verrou d'inactivité — 15 minutes.
 *
 * Fix 2026-08-27 : sync CROSS-ONGLETS via localStorage + BroadcastChannel.
 * L'ancien code utilisait `sessionStorage` (spécifique à chaque onglet), donc
 * un nouvel onglet ouvert AVANT le verrouillage restait ouvert alors que
 * l'onglet initial était verrouillé. Maintenant :
 *   - Le verrou est stocké dans `localStorage` (partagé entre tous les onglets)
 *   - Chaque changement est propagé via `BroadcastChannel('auth-sync')`
 *   - Une déconnexion (logout) propage un `type: 'logout'` qui déconnecte
 *     TOUS les onglets de tous les postes du même user (via `force_logout_at`
 *     côté backend + polling ; ici on gère juste le canal local).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { logout as logoutAction } from '../../store/slices/authSlice';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { API_URL } from '../../services/baseApi';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

// Clés localStorage partagées entre onglets
const LS_LOCKED = 'auth:session_locked';
const LS_LAST_ACTIVITY = 'auth:last_activity';
const LS_LOGOUT_SIGNAL = 'auth:logout_signal';

// Canal de sync inter-onglets (fallback storage event si absent)
type SyncMsg =
  | { type: 'lock' }
  | { type: 'unlock' }
  | { type: 'activity'; ts: number }
  | { type: 'logout' };

let _channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (_channel !== null) return _channel;
  try {
    _channel = new BroadcastChannel('auth-sync');
    return _channel;
  } catch { return null; }
}

function broadcast(msg: SyncMsg) {
  const c = getChannel();
  try { c?.postMessage(msg); } catch { /* */ }
}

const InactivityLock: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const [locked, setLockedState] = useState(() => {
    try { return localStorage.getItem(LS_LOCKED) === 'true'; }
    catch { return false; }
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const setLocked = useCallback((value: boolean, propagate = true) => {
    setLockedState(value);
    try {
      if (value) {
        localStorage.setItem(LS_LOCKED, 'true');
        localStorage.setItem('auth:session_locked_at', Date.now().toString());
      } else {
        localStorage.removeItem(LS_LOCKED);
        localStorage.removeItem('auth:session_locked_at');
        localStorage.setItem(LS_LAST_ACTIVITY, Date.now().toString());
      }
    } catch { /* */ }
    if (propagate) broadcast({ type: value ? 'lock' : 'unlock' });
  }, []);

  // Boot : vérifier lock + timeout inactivité en base localStorage
  useEffect(() => {
    try {
      const wasLocked = localStorage.getItem(LS_LOCKED) === 'true';
      if (wasLocked) {
        setLockedState(true);
        return;
      }
      const lastActivity = localStorage.getItem(LS_LAST_ACTIVITY);
      if (lastActivity) {
        const elapsed = Date.now() - parseInt(lastActivity);
        if (elapsed > INACTIVITY_TIMEOUT) setLocked(true);
      }
    } catch { /* */ }
  }, [setLocked]);

  // Écoute des événements cross-tabs (BroadcastChannel + storage fallback)
  useEffect(() => {
    const c = getChannel();
    const onMessage = (ev: MessageEvent<SyncMsg>) => {
      const msg = ev.data;
      if (!msg) return;
      if (msg.type === 'lock') setLockedState(true);
      else if (msg.type === 'unlock') setLockedState(false);
      else if (msg.type === 'activity') {
        // Un autre onglet a de l'activité — on réinitialise notre timer aussi
        if (!locked && timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            setLocked(true);
            setPassword(''); setError(''); setSuccess(false);
          }, INACTIVITY_TIMEOUT);
        }
      } else if (msg.type === 'logout') {
        // Un autre onglet a lancé un logout — on suit
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
          localStorage.removeItem(LS_LOCKED);
        } catch { /* */ }
        dispatch(logoutAction());
        window.location.href = '/login';
      }
    };
    c?.addEventListener('message', onMessage);

    // Fallback : storage event (BroadcastChannel indisponible)
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === LS_LOCKED) {
        setLockedState(ev.newValue === 'true');
      } else if (ev.key === LS_LOGOUT_SIGNAL && ev.newValue) {
        // Un autre onglet a signalé un logout global
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
        } catch { /* */ }
        dispatch(logoutAction());
        window.location.href = '/login';
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      c?.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [dispatch, locked, setLocked]);

  const resetTimer = useCallback(() => {
    if (locked) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    try { localStorage.setItem(LS_LAST_ACTIVITY, Date.now().toString()); } catch { /* */ }
    timerRef.current = setTimeout(() => {
      setLocked(true);
      setPassword(''); setError(''); setSuccess(false);
    }, INACTIVITY_TIMEOUT);
  }, [locked, setLocked]);

  // Débounce du broadcast d'activité (max 1×/30s pour ne pas spammer les onglets)
  const lastBroadcastRef = useRef(0);
  useEffect(() => {
    resetTimer();
    const handler = () => {
      resetTimer();
      const now = Date.now();
      if (now - lastBroadcastRef.current > 30_000) {
        lastBroadcastRef.current = now;
        broadcast({ type: 'activity', ts: now });
      }
    };
    EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach(ev => window.removeEventListener(ev, handler));
    };
  }, [resetTimer]);

  useEffect(() => {
    if (locked) setTimeout(() => inputRef.current?.focus(), 100);
  }, [locked]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !user) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user.username, password }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.access) {
          localStorage.setItem('auth_token', data.access);
          if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
        }
        setSuccess(true);
        setTimeout(() => {
          setLocked(false);
          setPassword('');
          setSuccess(false);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            setLocked(true);
            setPassword(''); setError(''); setSuccess(false);
          }, INACTIVITY_TIMEOUT);
        }, 600);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Mot de passe incorrect');
        setPassword('');
        inputRef.current?.focus();
      }
    } catch {
      setError('Erreur de connexion au serveur');
    }
    setLoading(false);
  };

  if (!locked || !user) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/80 backdrop-blur-md">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden transform transition-all duration-300 ${success ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 text-center">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            {success ? <CheckCircle size={28} className="text-green-300 animate-bounce" />
                     : <Lock size={28} className="text-white" />}
          </div>
          <h2 className="text-white font-bold text-lg">Session verrouillée</h2>
          <p className="text-blue-100 text-sm mt-1">15 minutes d'inactivité détectées</p>
        </div>
        <div className="px-6 py-5">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle size={40} className="text-green-500 mx-auto mb-2 animate-bounce" />
              <p className="text-green-700 dark:text-green-300 font-medium">Déverrouillé !</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center font-bold text-blue-700 dark:text-blue-300">
                  {(user.nom || user.username || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {user.nom && user.prenoms ? `${user.nom} ${user.prenoms}` : user.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{user.username}</p>
                </div>
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-500 shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}
              <form onSubmit={handleUnlock}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Mot de passe
                </label>
                <div className="relative mb-4">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={inputRef}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Entrez votre mot de passe"
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    autoComplete="current-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <button type="submit" disabled={loading || !password}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                           : <><Lock size={16} /> Déverrouiller</>}
                </button>
              </form>
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-4">
                Votre travail est préservé. Saisissez votre mot de passe pour continuer.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Broadcast un logout à tous les onglets. À appeler dans l'action logout Redux. */
export function broadcastLogoutToAllTabs() {
  broadcast({ type: 'logout' });
  try {
    localStorage.setItem(LS_LOGOUT_SIGNAL, Date.now().toString());
    // Retirer après 1s pour permettre re-signal futur
    setTimeout(() => { try { localStorage.removeItem(LS_LOGOUT_SIGNAL); } catch {} }, 1000);
  } catch { /* */ }
}

export default InactivityLock;
